import { useState, useCallback, useRef, useEffect } from 'react';
import { formatErrorMessage, isNetworkError } from '../utils/errors';

/**
 * Async operation state
 */
export interface AsyncState<T> {
  /** The data returned from the async operation */
  data: T | null;
  /** Whether the operation is currently loading */
  isLoading: boolean;
  /** Error message if the operation failed */
  error: string | null;
  /** Whether the operation has completed at least once (success or failure) */
  isSettled: boolean;
  /** Whether the last operation was successful */
  isSuccess: boolean;
  /** Whether the error was a network error */
  isNetworkError: boolean;
}

/**
 * Options for the useAsync hook
 */
export interface UseAsyncOptions<T> {
  /** Initial data value */
  initialData?: T | null;
  /** Callback when operation succeeds */
  onSuccess?: (data: T) => void;
  /** Callback when operation fails */
  onError?: (error: unknown) => void;
  /** Custom error message fallback */
  errorFallback?: string;
  /** Whether to reset error state when execute is called */
  resetErrorOnExecute?: boolean;
  /** Enable request cancellation on unmount */
  cancelOnUnmount?: boolean;
}

/**
 * Return type for useAsync hook
 */
export interface UseAsyncReturn<T, Args extends unknown[] = []> {
  /** Current state */
  state: AsyncState<T>;
  /** Execute the async operation */
  execute: (...args: Args) => Promise<T | null>;
  /** Reset state to initial values */
  reset: () => void;
  /** Set data manually */
  setData: (data: T | null) => void;
  /** Set error manually */
  setError: (error: string | null) => void;
  /** Clear error state */
  clearError: () => void;
  /** Retry the last operation */
  retry: () => Promise<T | null>;
  /** Get abort controller for current request */
  getAbortSignal: () => AbortSignal | undefined;
}

/**
 * A reusable hook for managing async operations with loading and error states.
 *
 * @example
 * ```tsx
 * const { state, execute, retry } = useAsync(
 *   async (userId: string) => {
 *     const result = await fetchUser(userId);
 *     return result.payload;
 *   },
 *   { onSuccess: (user) => console.log('Loaded user:', user.name) }
 * );
 *
 * useEffect(() => {
 *   execute('user-123');
 * }, [execute]);
 *
 * if (state.isLoading) return <LoadingSpinner />;
 * if (state.error) return <ErrorDisplay message={state.error} onRetry={retry} />;
 * if (state.data) return <UserProfile user={state.data} />;
 * ```
 */
export function useAsync<T, Args extends unknown[] = []>(
  asyncFn: (...args: Args) => Promise<T>,
  options: UseAsyncOptions<T> = {}
): UseAsyncReturn<T, Args> {
  const {
    initialData = null,
    onSuccess,
    onError,
    errorFallback = 'Something went wrong',
    resetErrorOnExecute = true,
    cancelOnUnmount = true,
  } = options;

  const [state, setState] = useState<AsyncState<T>>({
    data: initialData,
    isLoading: false,
    error: null,
    isSettled: false,
    isSuccess: false,
    isNetworkError: false,
  });

  // Store the last arguments for retry functionality
  const lastArgsRef = useRef<Args | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelOnUnmount && abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [cancelOnUnmount]);

  const execute = useCallback(
    async (...args: Args): Promise<T | null> => {
      // Store args for retry
      lastArgsRef.current = args;

      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      setState((prev) => ({
        ...prev,
        isLoading: true,
        error: resetErrorOnExecute ? null : prev.error,
        isNetworkError: false,
      }));

      try {
        const result = await asyncFn(...args);

        // Only update state if still mounted
        if (mountedRef.current) {
          setState({
            data: result,
            isLoading: false,
            error: null,
            isSettled: true,
            isSuccess: true,
            isNetworkError: false,
          });
          onSuccess?.(result);
        }

        return result;
      } catch (err) {
        // Don't update state if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          return null;
        }

        // Only update state if still mounted
        if (mountedRef.current) {
          const errorMessage = formatErrorMessage(err, errorFallback);
          const networkError = isNetworkError(err);

          setState({
            data: null,
            isLoading: false,
            error: errorMessage,
            isSettled: true,
            isSuccess: false,
            isNetworkError: networkError,
          });
          onError?.(err);
        }

        return null;
      }
    },
    [asyncFn, onSuccess, onError, errorFallback, resetErrorOnExecute]
  );

  const reset = useCallback(() => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setState({
      data: initialData,
      isLoading: false,
      error: null,
      isSettled: false,
      isSuccess: false,
      isNetworkError: false,
    });
    lastArgsRef.current = null;
  }, [initialData]);

  const setData = useCallback((data: T | null) => {
    setState((prev) => ({
      ...prev,
      data,
      isSuccess: data !== null,
    }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({
      ...prev,
      error,
      isSuccess: false,
    }));
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: null,
      isNetworkError: false,
    }));
  }, []);

  const retry = useCallback(async (): Promise<T | null> => {
    if (lastArgsRef.current !== null) {
      return execute(...lastArgsRef.current);
    }
    return null;
  }, [execute]);

  const getAbortSignal = useCallback(() => {
    return abortControllerRef.current?.signal;
  }, []);

  return {
    state,
    execute,
    reset,
    setData,
    setError,
    clearError,
    retry,
    getAbortSignal,
  };
}

/**
 * A hook for managing multiple async operations with individual loading states.
 * Useful for dashboards or components that load multiple pieces of data.
 *
 * @example
 * ```tsx
 * const { states, executeAll, execute } = useMultiAsync({
 *   user: () => fetchUser(userId),
 *   posts: () => fetchPosts(userId),
 *   comments: () => fetchComments(userId),
 * });
 *
 * useEffect(() => {
 *   executeAll(); // Load all data in parallel
 * }, [executeAll]);
 *
 * const isAnyLoading = Object.values(states).some(s => s.isLoading);
 * ```
 */
export function useMultiAsync<T extends Record<string, () => Promise<unknown>>>(
  asyncFns: T,
  options: Omit<UseAsyncOptions<unknown>, 'initialData'> = {}
): {
  states: { [K in keyof T]: AsyncState<Awaited<ReturnType<T[K]>>> };
  execute: <K extends keyof T>(key: K) => Promise<Awaited<ReturnType<T[K]>> | null>;
  executeAll: () => Promise<void>;
  reset: () => void;
  isAnyLoading: boolean;
  hasAnyError: boolean;
} {
  type StateMap = { [K in keyof T]: AsyncState<Awaited<ReturnType<T[K]>>> };

  const initialStates = Object.keys(asyncFns).reduce((acc, key) => {
    acc[key as keyof T] = {
      data: null,
      isLoading: false,
      error: null,
      isSettled: false,
      isSuccess: false,
      isNetworkError: false,
    };
    return acc;
  }, {} as StateMap);

  const [states, setStates] = useState<StateMap>(initialStates);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const execute = useCallback(
    async <K extends keyof T>(key: K): Promise<Awaited<ReturnType<T[K]>> | null> => {
      setStates((prev) => ({
        ...prev,
        [key]: { ...prev[key], isLoading: true, error: null },
      }));

      try {
        const result = await asyncFns[key]();

        if (mountedRef.current) {
          setStates((prev) => ({
            ...prev,
            [key]: {
              data: result,
              isLoading: false,
              error: null,
              isSettled: true,
              isSuccess: true,
              isNetworkError: false,
            },
          }));
        }

        return result as Awaited<ReturnType<T[K]>>;
      } catch (err) {
        if (mountedRef.current) {
          const errorMessage = formatErrorMessage(err, options.errorFallback);
          setStates((prev) => ({
            ...prev,
            [key]: {
              data: null,
              isLoading: false,
              error: errorMessage,
              isSettled: true,
              isSuccess: false,
              isNetworkError: isNetworkError(err),
            },
          }));
          options.onError?.(err);
        }
        return null;
      }
    },
    [asyncFns, options]
  );

  const executeAll = useCallback(async () => {
    const keys = Object.keys(asyncFns) as (keyof T)[];
    await Promise.all(keys.map((key) => execute(key)));
  }, [asyncFns, execute]);

  const reset = useCallback(() => {
    setStates(initialStates);
  }, [initialStates]);

  const isAnyLoading = Object.values(states).some((s) => s.isLoading);
  const hasAnyError = Object.values(states).some((s) => s.error !== null);

  return {
    states,
    execute,
    executeAll,
    reset,
    isAnyLoading,
    hasAnyError,
  };
}

export default useAsync;
